import { Button } from "@/components/ui/baseUi/button"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import { Text } from "@/components/ui/baseUi/text"
import { useCoachOffers } from "@/modules/coachOffers/hooks/useCoachOffers"
import { word } from "@modules/core/utils/i18n"
import { useCoachNavigate } from "@modules/navigation/hooks/useCoachNavigate"
import { Path } from "@modules/navigation/routes"
import { ArrowRight, CalendarRange, ExternalLink, Plus, Search, Tag } from "lucide-react"

export function CoachProgrammationPage() {
  const navigate = useCoachNavigate()
  const { data: offers } = useCoachOffers()

  return (
    <div className="relative flex min-h-full w-full flex-col gap-8 bg-surface-background-default p-6">
      <Text fontStyle="HS" className="font-semibold text-textOnSurface-default">
        {word("menu.programmation.detail")}
      </Text>

      {offers.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-10 rounded-xl bg-surface-background-secondary px-6 py-12">
          <div className="flex flex-col items-center gap-3 text-center">
            <Text fontStyle="HL" className="font-semibold text-textOnSurface-default">
              {word("coach.programmation.empty.title")}
            </Text>
            <Text fontStyle="BM" className="max-w-lg text-textOnSurface-subtle">
              {word("coach.programmation.empty.subtitle")}
            </Text>
          </div>

          <div className="flex w-117 flex-col gap-6">
            <button
              onClick={() => navigate(Path.Coach.Offers)}
              className="flex items-center justify-between gap-3 rounded-3xl bg-surface-background-default p-4 transition-colors hover:bg-surface-background-secondary"
            >
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-interactive-background-primary-default">
                  <Tag className="size-6 text-textOnSurface-primary-default" />
                </div>
                <div className="flex min-w-0 flex-col items-start text-left">
                  <Text fontStyle="TS" className="text-textOnSurface-default">
                    {word("coach.programmation.empty.step1.title")}
                  </Text>
                  <Text fontStyle="LS" className="text-textOnSurface-muted">
                    {word("coach.programmation.empty.step1.subtitle")}
                  </Text>
                </div>
              </div>
              <ExternalLink className="size-5 shrink-0 text-textOnSurface-subtle" />
            </button>

            <div className="flex cursor-not-allowed items-center gap-3 rounded-3xl bg-interactive-background-neutral-faded p-4 opacity-50">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-interactive-background-neutral-highlighted">
                <Tag className="size-6 text-textOnSurface-disabled" />
              </div>
              <div className="flex min-w-0 flex-col items-start">
                <Text fontStyle="TS" className="text-textOnSurface-disabled">
                  {word("coach.programmation.empty.step2.title")}
                </Text>
                <Text fontStyle="LS" className="text-textOnSurface-disabled">
                  {word("coach.programmation.empty.step2.subtitle")}
                </Text>
              </div>
            </div>

            <div className="flex cursor-not-allowed items-center gap-3 rounded-3xl bg-interactive-background-neutral-faded p-4 opacity-50">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-interactive-background-neutral-highlighted">
                <CalendarRange className="size-6 text-textOnSurface-disabled" />
              </div>
              <div className="flex min-w-0 flex-col items-start">
                <Text fontStyle="TS" className="text-textOnSurface-disabled">
                  {word("coach.programmation.empty.step3.title")}
                </Text>
                <Text fontStyle="LS" className="text-textOnSurface-disabled">
                  {word("coach.programmation.empty.step3.subtitle")}
                </Text>
              </div>
            </div>
          </div>

          <Button variant="primary" size="lg" onClick={() => navigate(Path.Coach.Offers)}>
            <Plus className="size-4" />
            {word("coach.programmation.empty.cta")}
          </Button>
        </div>
      ) : (
        <div className="relative flex flex-1 flex-col overflow-hidden">
          {/* content above pattern */}
          <div className="relative z-10 flex w-full flex-col gap-6 p-4 md:items-center md:p-8">
            <div className="w-full max-w-4xl rounded-3xl border border-borderDefault p-4 md:p-8">
              <InputGroup>
                <InputGroupAddon>
                  <Search className="size-4 text-textDefault-placeholder" />
                </InputGroupAddon>
                <InputGroupInput type="search" placeholder={word("coach.programmation.search.placeholder")} />
              </InputGroup>
            </div>

            <div className="flex w-full max-w-4xl flex-col gap-4 md:flex-row md:gap-6">
              <div className="flex flex-1 flex-col justify-between gap-4 rounded-3xl border border-borderDefault p-6 md:gap-6 md:p-8">
                <div className="flex flex-col gap-2">
                  <Text fontStyle="TM" className="text-textOnSurface-default">
                    {word("coach.programmation.type.accompanied.title")}
                  </Text>
                  <Text fontStyle="BS" className="text-textOnSurface-muted">
                    {word("coach.programmation.type.accompanied.subtitle")}
                  </Text>
                </div>
                <Button variant="primary" size="md" className="self-start">
                  {word("coach.programmation.type.accompanied.cta")}
                  <ArrowRight className="size-4" />
                </Button>
              </div>

              <div className="flex flex-1 flex-col justify-between gap-4 rounded-3xl border border-borderDefault p-6 md:gap-6 md:p-8">
                <div className="flex flex-col gap-2">
                  <Text fontStyle="TM" className="text-textOnSurface-default">
                    {word("coach.programmation.type.remote.title")}
                  </Text>
                  <Text fontStyle="BS" className="text-textOnSurface-muted">
                    {word("coach.programmation.type.remote.subtitle")}
                  </Text>
                </div>
                <Button variant="primary" size="md" className="self-start">
                  {word("coach.programmation.type.remote.cta")}
                  <ArrowRight className="size-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
